import { Dialog, Toolbar } from "client/Components";
import { localize } from "client_legacy/ndaw/culture";
import {
	ReactElement,
	useCallback,
	useState
} from "react";
import { FocalPointDialogContent } from "./FocalPointDialogContent";
import { useDataInputDialog } from "client/Hooks";
import { DEFAULT_FOCAL_POINT } from "./FocalPointEditorSection";
import { DocumentLocationViewModel } from "client/Api/Hub/hubSettings";
import { Base64FileUriUploadWithMetadataAndAttributionModel } from "client/Misc/commonTypes";

export type FocalPoint = {
	readonly left: number;
	readonly top: number;
};

export type ImageFileWithFocalPoint = {
	image: OptionalModel<Base64FileUriUploadWithMetadataAndAttributionModel>;
	focalPoint: FocalPoint;
};

export type FocalPointOptions = {
	supportedImageTypes: readonly string[];
	maxImageSize: number;
} & ({
	canSelectFromOnlineSources: true;
	supportedImageExtensions: readonly string[];
	defaultImageLocation: DocumentLocationViewModel | null;
	isPixabayImageGalleryAvailable: boolean;
} | {
	canSelectFromOnlineSources?: never;
});

export type FocalPointDialogProps = {
	onClose: () => void;
	onApply: (image: ImageFileWithFocalPoint) => void;
	initialImageUrl: string;
	initialFocalPoint: FocalPoint;
	options: FocalPointOptions;
	context: "creating-item" | "editing-item";
};

export const FocalPointDialog = (props: FocalPointDialogProps): ReactElement => {
	const {
		onClose,
		onApply,
		initialImageUrl,
		initialFocalPoint,
		options,
		context
	} = props;

	const [isBusy, setIsBusy] = useState<boolean>(false);

	const [imageWithFocalPoint, setImageWithFocalPoint] = useState<ImageFileWithFocalPoint>({
		image: { isSet: false },
		focalPoint: initialFocalPoint
	});

	const focalPointChangeHandler = useCallback(
		(focalPoint: FocalPoint): void => {
			setImageWithFocalPoint((x: ImageFileWithFocalPoint): ImageFileWithFocalPoint => ({
				...x,
				focalPoint
			}));
		},
		[]
	);

	const imageChangeHandler = useCallback(
		(image: Base64FileUriUploadWithMetadataAndAttributionModel): void => {
			setImageWithFocalPoint({
				focalPoint: DEFAULT_FOCAL_POINT,
				image: {
					isSet: true,
					value: image
				}
			});
		},
		[]
	);

	const applyHandler = useCallback(
		(): void => {
			onApply(imageWithFocalPoint);
		},
		[imageWithFocalPoint, onApply]
	);

	const imageUri: string = imageWithFocalPoint.image
		.isSet
			? imageWithFocalPoint.image
				.value
				.uri
			: initialImageUrl;

	return (
		<Dialog
			trackingId="edit-image-focal-point-dialog"
			variant="left-slider"
			onClose={onClose}
			hasBusyIndicator={isBusy}
		>
			<Dialog.Header
				hasBorder
				title={localize("_EditFocalPoint")}
				onClose={onClose}
			/>
			<Dialog.Body goesEdgeToEdge>
				<FocalPointDialogContent
					value={imageWithFocalPoint.focalPoint}
					uri={imageUri}
					onChange={focalPointChangeHandler}
					onImageChange={imageChangeHandler}
					options={options}
					onIsBusyChange={setIsBusy}
				/>
			</Dialog.Body>
			<Dialog.Footer hasBorder>
				<Toolbar>
					<Toolbar.Button
						trackingId="apply"
						text={
							localize(
								context === "creating-item"
									? "_Apply"
									: "_Save"
							)
						}
						onClick={applyHandler}
						kind="primary"
					/>
					<Toolbar.Button
						trackingId="skip"
						text={
							localize(
								context === "creating-item"
									? "_Skip"
									: "_Cancel"
							)
						}
						onClick={onClose}
						kind="secondary"
					/>
				</Toolbar>
			</Dialog.Footer>
		</Dialog>
	);
};
FocalPointDialog.displayName = "FocalPointDialog";

export type FocalPointDialogInitialData = Omit<FocalPointDialogProps, "onClose" | "onApply">;

export function useFocalPointDialog(): [
	element: ReactElement | null,
	open: (props: FocalPointDialogInitialData) => Promise<TryResult<ImageFileWithFocalPoint>>
] {
	return useDataInputDialog<FocalPointDialogInitialData, ImageFileWithFocalPoint>(
		(
			props: FocalPointDialogInitialData,
			onClose: () => void,
			onSave: (result: ImageFileWithFocalPoint) => void
		) => (
			<FocalPointDialog
				{...props}
				onClose={onClose}
				onApply={onSave}
			/>
		)
	);
}